import React, { Fragment, useState, useContext, useRef, useEffect } from 'react';
import classnames from 'classnames';
import styles from './styles.module.scss';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  ButtonGroup,
  Icons,
  InputBox,
  ListView,
  ListViewColumn,
  Modal,
  RadioButton,
  Switch,
  Tooltip,
} from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import { CoreUser, CoreUserOrganization } from '@densityco/lib-api-types';

import filterCollection from '../../helpers/filter-collection';
import { ROLE_INFO } from '../../helpers/permissions';
import { getInitialState, ImpersonateState, saveImpersonateData } from './state';

import { TokenProviderContext } from '../TokenProvider';
import { ImpersonateData, DEFAULT_IMPERSONATE } from '../../helpers/session';

const orgFilterHelper = filterCollection<CoreUserOrganization>({ fields: ['name'] });
const userFilterHelper = filterCollection<CoreUser>({ fields: ['email', 'full_name'] });

const Impersonate: React.FunctionComponent<{
  onChange?: (data: ImpersonateData) => void;
}> = ({ onChange = () => window.location.reload() }) => {
  const context = useContext(TokenProviderContext);
  if (!context) {
    throw new Error('Impersonate button must be put inside of a "TokenProvider" component!');
  }
  const { token, tokenCheckResponse, setXImpersonateUserHeader, loginHost } = context;

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const [state, setState] = useState(getInitialState());
  const [visible, setVisible] = useState(false);

  function showModal() {
    const initialState = { ...getInitialState(), enabled: true };
    setState(initialState);
    setVisible(true);

    // Load initial data
    loadOrganizations(initialState.organizations.selected);
    if (initialState.organizations.selected && initialState.users.selected) {
      loadUsers(initialState.organizations.selected.id, initialState.users.selected);
    }
  }
  function hideModal() {
    setVisible(false);
  }

  function updateImpersonateData(impersonateData: ImpersonateData) {
    saveImpersonateData(impersonateData);
    setXImpersonateUserHeader(
      impersonateData !== DEFAULT_IMPERSONATE && impersonateData.enabled ? impersonateData.id : undefined
    );
    if (onChange) {
      onChange(impersonateData);
    }
    hideModal();
  }

  function submitModal() {
    if (state.organizations.status === 'IDLE' && state.users.status === 'IDLE') {
      const impersonateData: ImpersonateData =
        state.organizations.selected && state.users.selected
          ? {
              enabled: true,
              id: state.users.selected.id,
              email: state.users.selected.email,
              name: state.users.selected.full_name,
              role: state.users.selected.role,
              organizationId: state.organizations.selected.id,
              organizationName: state.organizations.selected.name,
            }
          : DEFAULT_IMPERSONATE;

      updateImpersonateData(impersonateData);
    }
  }

  async function submitOrgCreation() {
    const newOrgName = state.organizations.search;

    const response = await fetch(`${loginHost}/v2/organizations/create_with_service_user`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: newOrgName,
        description: newOrgName,
      }),
    });

    if (!response.ok) {
      setState((s) => ({
        ...s,
        createOrganizationError: `Unable to create new org ${newOrgName}, please try again.`,
      }));
      return;
    }

    const data = await response.json();

    const impersonateData: ImpersonateData = {
      enabled: true,
      id: data.service_user.id,
      email: data.service_user.email,
      name: data.service_user.full_name,
      role: data.service_user.role,
      organizationId: data.id,
      organizationName: data.name,
    };
    updateImpersonateData(impersonateData);
  }

  async function loadOrganizations(selected: ImpersonateState['organizations']['selected']) {
    setState((s) => ({
      ...s,
      organizations: {
        status: 'LOADING',
        selected: null,
        data: [],
        search: '',
      },
    }));

    const response = await fetch(`${loginHost}/v2/organizations`, { headers });

    if (!response.ok) {
      setState((s) => ({
        ...s,
        organizations: {
          status: 'ERROR',
          selected: null,
          data: [],
          search: '',
        },
      }));
      return;
    }

    const data = await response.json();
    setState((s) => ({
      ...s,
      organizations: {
        status: 'IDLE',
        data,
        selected,
        search: '',
      },
    }));
  }

  async function loadUsers(organizationId: string, selected: ImpersonateState['users']['selected']) {
    setState((s) => ({
      ...s,
      users: { status: 'LOADING', selected: null, data: [], search: '' },
    }));

    const response = await fetch(`${loginHost}/v2/users?organization_id=${organizationId}`, { headers });
    if (!response.ok) {
      setState((s) => ({
        ...s,
        users: { status: 'ERROR', selected: null, data: [], search: '' },
      }));
      return;
    }
    const data = await response.json();
    setState((s) => ({
      ...s,
      users: {
        status: 'IDLE',
        data,
        selected,
        search: '',
      },
    }));
  }

  function onSelectOrganization(organization: CoreUserOrganization) {
    if (state.organizations.status !== 'IDLE') {
      return;
    }
    if (state.users.status !== 'IDLE') {
      return;
    }

    setState({
      ...state,
      organizations: {
        ...state.organizations,
        selected: {
          id: organization.id,
          name: organization.name,
        },
      },
      users: {
        ...state.users,
        selected: null,
      },
    });
    loadUsers(organization.id, null);
  }

  function onSelectUser(user: CoreUser) {
    if (state.users.status !== 'IDLE') {
      return;
    }
    setState({
      ...state,
      users: {
        ...state.users,
        selected: user
          ? {
              id: user.id,
              email: user.email,
              full_name: user.full_name,
              role: user.role,
            }
          : null,
      },
    });
  }
  const filteredOrgs: Array<CoreUserOrganization> =
    state.organizations.status === 'IDLE' ? orgFilterHelper(state.organizations.data, state.organizations.search) : [];
  const filteredUsers =
    state.users.status === 'IDLE'
      ? userFilterHelper(
          state.users.data.filter((x) => x.role !== 'service'),
          state.users.search
        )
      : [];

  // Focus the "org" input when the modal is newly visible
  const orgInputRef = useRef<HTMLInputElement>();
  useEffect(() => {
    visible && orgInputRef.current && orgInputRef.current.focus();
  }, [visible]);

  // Focus the "user" input and "save" button when needed
  const userInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  // Hide the impersonate button if the user cannot impersonate.
  const impersonateButtonVisible = tokenCheckResponse ? tokenCheckResponse.permissions.includes('impersonate') : false;
  if (!impersonateButtonVisible) {
    return null;
  }

  return (
    <Fragment>
      {/* NOTE: The button reflects the localstorage state, not the current state of the modal */}
      <div onClick={() => showModal()}>
        {state.impersonateData.enabled ? (
          <Tooltip
            placement="bottom-end"
            contents={`${state.impersonateData.organizationName}: ${state.impersonateData.email}`}
            target={
              <li className={styles.impersonateDefaultIcon}>
                <Icons.ImpersonateOn color={colorVariables.midnight} />
              </li>
            }
          />
        ) : (
          <li className={styles.impersonateDefaultIcon}>
            <Icons.ImpersonateOff color="currentColor" />
          </li>
        )}
      </div>

      <Modal width={800} height={600} visible={visible} onBlur={() => hideModal()} onEscape={() => hideModal()}>
        <div className={styles.impersonateModal}>
          <AppBar>
            <AppBarSection>
              <AppBarTitle>User Impersonation</AppBarTitle>
            </AppBarSection>
            <AppBarSection>
              <Switch
                value={state.enabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (state.organizations.status === 'LOADING' || state.organizations.status === 'ERROR') {
                    return;
                  }
                  if (state.users.status === 'LOADING' || state.users.status === 'ERROR') {
                    return;
                  }
                  setState({
                    ...state,
                    enabled: event.target.checked,
                    organizations: {
                      ...state.organizations,
                      status: 'IDLE',
                      selected: state.organizations.selected,
                    },
                    users: {
                      ...state.users,
                      status: 'IDLE',
                      selected: null,
                    },
                  });
                }}
              />
            </AppBarSection>
          </AppBar>
          <div style={{ display: 'flex', height: 470 }}>
            <div
              style={{
                width: '50%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: `1px solid ${colorVariables.gray300}`,
              }}
            >
              <AppBarContext.Provider value="CARD_HEADER">
                <AppBar>
                  <AppBarTitle>Organization</AppBarTitle>
                </AppBar>
              </AppBarContext.Provider>
              <AppBar>
                {state.organizations.status === 'IDLE' ? (
                  <InputBox
                    ref={orgInputRef}
                    width="100%"
                    placeholder='ex: "Density Dev", "Acme Co"'
                    leftIcon={<Icons.Search color={colorVariables.gray400} />}
                    disabled={!state.enabled}
                    value={state.organizations.search}
                    onKeyPress={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' && filteredOrgs.length > 0) {
                        if (state.organizations.status !== 'IDLE') {
                          return;
                        }
                        const org = state.organizations.data.find((x: CoreUserOrganization) => x.id === filteredOrgs[0].id);
                        if (!org) {
                          return;
                        }

                        userInputRef.current && userInputRef.current.focus();

                        onSelectOrganization(org);
                      }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (state.organizations.status !== 'IDLE') {
                        return;
                      }
                      setState({
                        ...state,
                        organizations: {
                          ...state.organizations,
                          search: e.target.value,
                        },
                        createOrganizationError: null,
                      });
                    }}
                  />
                ) : (
                  <InputBox
                    type="text"
                    value=""
                    readOnly
                    width="100%"
                    placeholder='ex: "Density Dev", "Acme Co"'
                    leftIcon={<Icons.Search color={colorVariables.gray400} />}
                  />
                )}
              </AppBar>
              {state.organizations.status === 'LOADING' ? <div className={styles.centered}>Loading...</div> : null}
              {state.organizations.status === 'ERROR' ? (
                <div className={classnames(styles.centered, styles.error)}>Error loading data.</div>
              ) : null}
              {state.organizations.status === 'IDLE' ? (
                <div
                  style={{
                    flexGrow: 1,
                    padding: '0 24px',
                    overflowY: state.enabled ? 'scroll' : 'hidden',
                  }}
                >
                  <ListView data={filteredOrgs} showHeaders={false}>
                    <ListViewColumn
                      id="Column 1"
                      title=""
                      width="auto"
                      disabled={(item: CoreUserOrganization) => state.organizations.status !== 'IDLE' || !state.enabled}
                      onClick={(item: CoreUserOrganization) => onSelectOrganization(item)}
                      template={(item: CoreUserOrganization) => (
                        <div style={{ opacity: state.enabled ? 1.0 : 0.25 }}>
                          <RadioButton
                            name="modal-impersonate-organization"
                            checked={state.organizations.selected ? state.organizations.selected.id === item.id : false}
                            disabled={state.organizations.status !== 'IDLE' || !state.enabled}
                            value={item.id}
                            text={item.name}
                            onChange={() => null}
                          />
                        </div>
                      )}
                    />
                  </ListView>
                  {/* If no organizations match the search term, allow for creation */}
                  {state.enabled &&
                  state.organizations.search.length &&
                  !filteredOrgs.some((o) => o.name.toLowerCase() === state.organizations.search.toLowerCase()) ? (
                    <Button
                      ref={saveButtonRef}
                      variant="underline"
                      type="primary"
                      style={{
                        width: '100%',
                        margin: '16px 0',
                        height: 'auto',
                      }}
                      disabled={state.organizations.status !== 'IDLE' || !state.enabled}
                      onClick={() => submitOrgCreation()}
                    >
                      <span className={styles.createOrgButtonText}>
                        {state.createOrganizationError
                          ? state.createOrganizationError
                          : `Create new Org "${state.organizations.search}" for scoping`}
                      </span>
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div
              style={{
                width: '50%',
                display: 'flex',
                flexDirection: 'column',
                background: colorVariables.gray000,
              }}
            >
              <div style={{ background: '#FFF' }}>
                <AppBarContext.Provider value="CARD_HEADER">
                  <AppBar>
                    <AppBarTitle>User</AppBarTitle>
                  </AppBar>
                </AppBarContext.Provider>
              </div>
              <AppBar>
                {state.users.status === 'IDLE' ? (
                  <InputBox
                    ref={userInputRef}
                    width="100%"
                    placeholder='ex: "John Denver"'
                    leftIcon={<Icons.Search color={colorVariables.gray400} />}
                    disabled={!state.enabled}
                    value={state.users.search}
                    onKeyPress={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' && filteredUsers.length > 0) {
                        if (state.users.status !== 'IDLE') {
                          return;
                        }
                        const user = state.users.data.find((x) => x.id === filteredUsers[0].id);
                        if (!user) {
                          return;
                        }
                        onSelectUser(user);
                        setTimeout(() => saveButtonRef.current && saveButtonRef.current.focus());
                      }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (state.users.status !== 'IDLE') {
                        return;
                      }
                      setState({
                        ...state,
                        users: {
                          ...state.users,
                          search: e.target.value,
                        },
                      });
                    }}
                  />
                ) : (
                  <InputBox
                    type="text"
                    value=""
                    readOnly
                    width="100%"
                    placeholder='ex: "John Denver"'
                    leftIcon={<Icons.Search color={colorVariables.gray400} />}
                  />
                )}
              </AppBar>

              {state.users.status === 'LOADING' ? <div className={styles.centered}>Loading...</div> : null}
              {state.users.status === 'ERROR' ? (
                <div className={classnames(styles.centered, styles.error)}>Error loading data.</div>
              ) : null}
              {state.users.status === 'IDLE' ? (
                <Fragment>
                  <div
                    style={{
                      flexGrow: 1,
                      padding: '12px 24px',
                      overflowY: state.enabled ? 'scroll' : 'hidden',
                    }}
                  >
                    {filteredUsers.map((item: CoreUser) => {
                      const label = item.full_name || item.email;
                      const selected = state.users.selected ? state.users.selected.id === item.id : false;
                      const disabled = !state.enabled;
                      return (
                        <div
                          key={item.id}
                          className={styles.impersonateUserListItem}
                          style={{
                            opacity: state.enabled ? 1.0 : 0.25,
                            cursor: state.enabled ? 'pointer' : undefined,
                            color: selected ? colorVariables.midnight : undefined,
                          }}
                          onClick={() => {
                            if (state.users.status !== 'IDLE') {
                              return;
                            }
                            const user = state.users.data.find((x) => x.id === item.id);
                            if (!user) {
                              return;
                            }
                            onSelectUser(user);
                          }}
                        >
                          <div className={styles.impersonateUserIcon}>
                            {(item.full_name || '')
                              .split(' ')
                              .slice(0, 2)
                              .filter((word: string) => word.length > 0)
                              .map((word: string) => word[0].toUpperCase())
                              .join('')}
                          </div>
                          <div style={{ flexGrow: 1 }}>
                            {label.slice(0, 24)}
                            {label.length > 24 ? '...' : ''} ({ROLE_INFO[item.role].label})
                          </div>
                          <RadioButton
                            name="modal-impersonate-user"
                            checked={selected}
                            disabled={disabled}
                            value={item.id}
                            onChange={() => null}
                          />
                        </div>
                      );
                    })}
                    {filteredUsers.length === 0 ? (
                      state.organizations.selected ? (
                        <div className={styles.centered}>No users found.</div>
                      ) : state.organizations.status !== 'LOADING' ? (
                        <div className={styles.centered}>Please select an organization.</div>
                      ) : null
                    ) : null}
                  </div>
                </Fragment>
              ) : null}
            </div>
          </div>
          <AppBarContext.Provider value="BOTTOM_ACTIONS">
            <AppBar>
              <AppBarSection></AppBarSection>
              <AppBarSection>
                <ButtonGroup>
                  <Button variant="underline" onClick={() => hideModal()}>
                    Cancel
                  </Button>
                  <Button
                    ref={saveButtonRef}
                    variant="filled"
                    type="primary"
                    disabled={
                      state.organizations.status !== 'IDLE' ||
                      state.users.status !== 'IDLE' ||
                      (state.enabled && !state.users.selected)
                    }
                    onClick={() => submitModal()}
                  >
                    Save settings
                  </Button>
                </ButtonGroup>
              </AppBarSection>
            </AppBar>
          </AppBarContext.Provider>
        </div>
      </Modal>
    </Fragment>
  );
};

Impersonate.displayName = 'Impersonate';
export default Impersonate;
